import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/api/client";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";


const FLAGS = [
  { key: "funding_readiness_assessment", label: "Funding Readiness Assessment", help: "Hides /readiness, the dashboard widget and any readiness CTAs." },
  { key: "credit_report_upload", label: "Credit Report Upload", help: "Hides the upload form on /credit-report. Existing reports stay visible." },
  { key: "ocr_extraction", label: "OCR / Extraction Pipeline", help: "Cosmetic — when OFF, /credit-report shows a maintenance banner." },
  { key: "ai_credit_report_analysis", label: "AI Credit Report Analysis", help: "Cosmetic — when OFF, the analyzer banner shows on /credit-report." },
  { key: "dispute_letter_generator", label: "Dispute Letter Generator", help: "Hides Generate-letter CTAs and the /dispute-letters nav. The route itself shows a disabled empty state." },
  { key: "business_funding_module", label: "Business Funding Module", help: "Hides business-track partners and the business path on /readiness." },
  { key: "startup_funding_module", label: "Startup Funding Module", help: "Hides startup-track partners. Used in 8b for finer control." },
  { key: "business_formation_link", label: "Business Formation Link", help: "Hides the business-formation referral card on /readiness." },
  { key: "affiliate_program", label: "Affiliate Program", help: "Hides the referral card on /dashboard." },
  { key: "admin_site_settings", label: "Admin Site Settings", help: "If OFF, the /admin/site-settings nav and route show a disabled state." },
  { key: "credit_engine_v2", label: "Credit Engine v2 — dual-write (dark)", help: "V2.1 Track 2 dark launch. When ON, every credit-report import writes to the new per_bureau_tradelines / credit_reports_v2 stack alongside legacy. No user-facing change. Reads still hit legacy." },
  { key: "credit_analyst_v2", label: "Credit Analyst v2 — AI explanations (dark)", help: "V2.1 Phase 5A. When ON, every V2 import runs the AI Credit Analyst (GPT-5.2) on each detected finding, producing 5 explanations grounded in structured JSON only. Explanations are stored in credit_analyst_explanations but not surfaced to users yet — QA review only via /admin/credit-engine/analyst-preview." },
  { key: "landing_v2_enabled", label: "Show new landing page (v2) as default", help: "Reversible A/B preview. When ON, `/` renders the new brand-direction landing (Executive Purple + Deep Aubergine). `/landing-v1` and `/landing-v2` stay reachable regardless — flip this off at any time to instantly revert the default." },
];

export default function AdminFeatureFlags() {
  const { flags: liveFlags } = useFeatureFlags();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/feature-flags");
      setDoc(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load feature flags.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key, currentVal) => {
    setSavingKey(key);
    setError("");
    try {
      const res = await api.patch("/admin/feature-flags", { [key]: !currentVal });
      setDoc(res.data);
      // Live broadcast: also patch the in-memory context so this admin sees the
      // change everywhere else in the app immediately.
      Object.assign(liveFlags, { ...liveFlags, [key]: !currentVal });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update flag.");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <AdminLayout
      title="Feature flags"
      subtitle="Disabling a feature hides it from end users immediately on their next page load. Use sparingly."
      actions={
        <button
          type="button"
          onClick={load}
          data-testid="admin-flags-refresh"
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--v2-border)] px-3 py-1.5 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)] hover:bg-[var(--v2-surface)]"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      }
    >
      <div className="mb-4 max-w-3xl">
        <AdminPageHelp
          title="Feature Flags"
          purpose="Toggle major product surfaces on and off without redeploying code."
          configures="Flag state (on/off) for each module (credit report upload, dispute generator, readiness assessment, etc.)."
          impact="Turning a flag OFF immediately hides the affected pages and shows the temporarily-disabled empty state for signed-in members."
          safety="Turning flags back ON is instant and reversible. Members mid-flow see the disabled state on next navigation."
        />
      </div>
      <div
        className="mb-5 rounded-md border p-3 flex items-start gap-2 text-xs"
        style={{ borderColor: "#FBBF2466", background: "rgba(251,191,36,0.05)", color: "var(--v2-text-muted)" }}
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#FBBF24" }} />
        <p>
          Toggles take effect on the next page load for every user. The frontend caches the flag state in
          context for the duration of the session — sign out / refresh after changing a flag if you want to
          verify the consumer experience yourself.
        </p>
      </div>

      {error && (
        <div data-testid="admin-flags-error" className="mb-3 text-sm" style={{ color: "#F87171" }}>{error}</div>
      )}

      <div
        data-testid="admin-flags-list"
        className="rounded-xl border border-[var(--v2-border)] overflow-hidden"
        style={{ background: "var(--v2-surface)" }}
      >
        {loading && !doc ? (
          <div className="p-5 text-sm text-[var(--v2-text-muted)]">Loading…</div>
        ) : (
          <ul>
            {FLAGS.map(({ key, label, help }, i) => {
              const value = doc?.[key] ?? true;
              // Alias for spec-referenced tests. `landing_v2_enabled`
              // is dispatch-referenced by `admin-landing-v2-toggle` in
              // addition to the generic `flag-toggle-<key>` id.
              const toggleTestId =
                key === "landing_v2_enabled"
                  ? "admin-landing-v2-toggle"
                  : `flag-toggle-${key}`;
              return (
                <li
                  key={key}
                  data-testid={`flag-row-${key}`}
                  className="flex items-start gap-4 px-5 py-4"
                  style={{ borderTop: i === 0 ? "none" : "1px solid var(--v2-border)" }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--v2-text)] font-medium">{label}</div>
                    <div className="text-xs text-[var(--v2-text-muted)] mt-0.5">{help}</div>
                    <div className="text-[10px] text-[var(--v2-text-muted)] mt-1.5 font-mono">{key}</div>
                  </div>
                  <Toggle
                    testId={toggleTestId}
                    checked={value}
                    disabled={savingKey === key}
                    onChange={() => toggle(key, value)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {doc?.updated_at && (
        <p className="mt-4 text-[11px] text-[var(--v2-text-muted)]">
          Last edited: {new Date(doc.updated_at).toLocaleString()}{doc.updated_by ? ` · by ${doc.updated_by}` : ""}
        </p>
      )}
    </AdminLayout>
  );
}

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={checked ? "Feature enabled — click to disable" : "Feature disabled — click to enable"}
      disabled={disabled}
      onClick={onChange}
      data-testid={testId}
      className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none disabled:opacity-50"
      style={{
        background: checked ? "#B794F4" : "rgba(255,255,255,0.1)",
      }}
    >
      <span
        className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5"
        style={{ transform: `translateX(${checked ? 22 : 2}px)` }}
      />
    </button>
  );
}
